import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell, AreaChart, Area } from 'recharts';
import { TrendingUp, Users, FileText, Clock, AlertTriangle, CheckCircle, Loader, Download, Eye, ChevronLeft, ChevronRight, Search, RefreshCw } from 'lucide-react';

const API_BASE = '';
const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

// ============================================================================
// DASHBOARD OVERVIEW CARDS
// ============================================================================

function DashboardOverviewCards({ data, loading }) {
  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {[1, 2, 3, 4].map(i => (
          <Card key={i} className="animate-pulse">
            <CardContent className="pt-6">
              <div className="h-8 bg-gray-200 rounded w-1/2 mb-2"></div>
              <div className="h-4 bg-gray-200 rounded w-3/4"></div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  const cards = [
    {
      title: 'Total Merchants',
      value: data?.total_merchants || 0,
      icon: Users,
      color: 'bg-blue-100 text-blue-600'
    },
    {
      title: 'Completed',
      value: data?.completed || 0,
      icon: CheckCircle,
      color: 'bg-green-100 text-green-600'
    },
    {
      title: 'High Risk',
      value: data?.high_risk || 0,
      icon: AlertTriangle,
      color: 'bg-red-100 text-red-600'
    },
    {
      title: 'Avg Processing Time',
      value: `${data?.avg_processing_time || 0}s`,
      icon: Clock,
      color: 'bg-purple-100 text-purple-600'
    }
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      {cards.map((card, idx) => {
        const Icon = card.icon;
        return (
          <Card key={idx}>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{card.title}</p>
                  <p className="text-2xl font-bold mt-2">{card.value}</p>
                </div>
                <div className={`p-3 rounded-lg ${card.color}`}>
                  <Icon className="w-6 h-6" />
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

// ============================================================================
// TIME SERIES CHART
// ============================================================================

function TimeSeriesChart({ data, period, onPeriodChange, loading }) {
  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Processing Timeline</CardTitle>
        </CardHeader>
        <CardContent className="h-64 flex items-center justify-center">
          <Loader className="w-8 h-8 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle>Processing Timeline</CardTitle>
          <CardDescription>Merchants processed over time ({period})</CardDescription>
        </div>
        <div className="flex gap-2">
          {['hourly', 'daily', 'weekly', 'monthly'].map(p => (
            <Button
              key={p}
              variant={period === p ? 'default' : 'outline'}
              size="sm"
              onClick={() => onPeriodChange(p)}
              className="capitalize"
            >
              {p}
            </Button>
          ))}
        </div>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={data || []}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Line type="monotone" dataKey="merchants_processed" stroke="#3b82f6" name="Processed" />
            <Line type="monotone" dataKey="merchants_failed" stroke="#ef4444" name="Failed" />
            <Line type="monotone" dataKey="mcc_classified" stroke="#10b981" name="MCC Classified" />
          </LineChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}

// ============================================================================
// WORKFLOW STATISTICS
// ============================================================================

function WorkflowTimelineAnalytics({ data, period, onPeriodChange, days, onDaysChange, loading }) {
  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Workflow Approval Timeline</CardTitle>
        </CardHeader>
        <CardContent className="h-96 flex items-center justify-center">
          <Loader className="w-8 h-8 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  const periodOptions = [
    { value: 'daily', label: 'Daily', defaultDays: 30 },
    { value: 'weekly', label: 'Weekly', defaultDays: 90 },
    { value: 'monthly', label: 'Monthly', defaultDays: 180 },
    { value: 'quarterly', label: 'Quarterly', defaultDays: 365 },
    { value: 'yearly', label: 'Yearly', defaultDays: 730 }
  ];

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Workflow Approval Timeline</CardTitle>
            <CardDescription>Track approval trends over time</CardDescription>
          </div>
          <div className="flex gap-2">
            {periodOptions.map(opt => (
              <Button
                key={opt.value}
                variant={period === opt.value ? 'default' : 'outline'}
                size="sm"
                onClick={() => {
                  onPeriodChange(opt.value);
                  onDaysChange(opt.defaultDays);
                }}
              >
                {opt.label}
              </Button>
            ))}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          {/* Line Chart - Approval Trends */}
          <div>
            <h3 className="text-sm font-semibold mb-3">Approval Trends</h3>
            <ResponsiveContainer width="100%" height={190}>
              <AreaChart data={data}>
                <defs>
                  <linearGradient id="wfApproved" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.35} />
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0.05} />
                  </linearGradient>
                  <linearGradient id="wfRejected" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#ef4444" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#ef4444" stopOpacity={0.05} />
                  </linearGradient>
                  <linearGradient id="wfProgress" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.25} />
                    <stop offset="95%" stopColor="#f59e0b" stopOpacity={0.05} />
                  </linearGradient>
                  <linearGradient id="wfReview" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.25} />
                    <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0.05} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#6b7280' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: '#6b7280' }} axisLine={false} tickLine={false} allowDecimals={false} />
                <Tooltip />
                <Area type="monotone" dataKey="approved" stroke="#10b981" strokeWidth={2} fill="url(#wfApproved)" name="Approved" dot={false} />
                <Area type="monotone" dataKey="rejected" stroke="#ef4444" strokeWidth={2} fill="url(#wfRejected)" name="Rejected" dot={false} />
                <Area type="monotone" dataKey="in_progress" stroke="#f59e0b" strokeWidth={2} fill="url(#wfProgress)" name="In Progress" dot={false} />
                <Area type="monotone" dataKey="further_review" stroke="#8b5cf6" strokeWidth={2} fill="url(#wfReview)" name="Further Review" dot={false} />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          {/* Bar Chart - Status Breakdown */}
          <div>
            <h3 className="text-sm font-semibold mb-3">Status Breakdown by Period</h3>
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={data} barSize={18}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#6b7280' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: '#6b7280' }} axisLine={false} tickLine={false} allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="approved" fill="#10b981" radius={[6, 6, 0, 0]} name="Approved" />
                <Bar dataKey="rejected" fill="#ef4444" radius={[6, 6, 0, 0]} name="Rejected" />
                <Bar dataKey="in_progress" fill="#f59e0b" radius={[6, 6, 0, 0]} name="In Progress" />
                <Bar dataKey="further_review" fill="#8b5cf6" radius={[6, 6, 0, 0]} name="Further Review" />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Summary Stats */}
          <div className="grid grid-cols-4 gap-4 pt-4 border-t">
            <div className="text-center">
              <p className="text-2xl font-bold text-green-600">
                {data.reduce((sum, d) => sum + (d.approved || 0), 0)}
              </p>
              <p className="text-xs text-muted-foreground">Total Approved</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-red-600">
                {data.reduce((sum, d) => sum + (d.rejected || 0), 0)}
              </p>
              <p className="text-xs text-muted-foreground">Total Rejected</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-amber-600">
                {data.reduce((sum, d) => sum + (d.in_progress || 0), 0)}
              </p>
              <p className="text-xs text-muted-foreground">In Progress</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-purple-600">
                {data.reduce((sum, d) => sum + (d.further_review || 0), 0)}
              </p>
              <p className="text-xs text-muted-foreground">Further Review</p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function WorkflowStats({ data, loading }) {
  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Workflow Approvals</CardTitle>
        </CardHeader>
        <CardContent className="h-64 flex items-center justify-center">
          <Loader className="w-8 h-8 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  const chartData = [
    { name: 'Pending', value: data?.pending_approval || 0 },
    { name: 'Approved', value: data?.approved || 0 },
    { name: 'Rejected', value: data?.rejected || 0 }
  ];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <Card>
        <CardHeader>
          <CardTitle>Approval Status</CardTitle>
          <CardDescription>Workflow instances by status</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm">Total Workflows</span>
              <Badge variant="outline">{data?.total_workflows || 0}</Badge>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-amber-600">Pending Approval</span>
              <Badge className="bg-amber-100 text-amber-800">{data?.pending_approval || 0}</Badge>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-green-600">Approved</span>
              <Badge className="bg-green-100 text-green-800">{data?.approved || 0}</Badge>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-red-600">Rejected</span>
              <Badge className="bg-red-100 text-red-800">{data?.rejected || 0}</Badge>
            </div>
            <hr className="my-3" />
            <div className="flex justify-between items-center">
              <span className="text-sm font-semibold">Avg Approval Time</span>
              <span className="font-semibold">{data?.avg_approval_time_hours || 0}h</span>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Status Distribution</CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, value }) => `${name}: ${value}`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    </div>
  );
}

// ============================================================================
// MERCHANT PROCESSING TABLE
// ============================================================================

function MerchantProcessingTable({ merchants, loading, onRefresh }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 15;

  const filteredMerchants = merchants.filter(m =>
    m.merchant_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    m.merchant_url?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const paginatedMerchants = filteredMerchants.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const totalPages = Math.ceil(filteredMerchants.length / itemsPerPage);

  const statusColors = {
    completed: 'bg-green-100 text-green-800',
    failed: 'bg-red-100 text-red-800',
    pending: 'bg-yellow-100 text-yellow-800',
    processing: 'bg-blue-100 text-blue-800',
    retry: 'bg-orange-100 text-orange-800'
  };

  const riskColors = {
    high: 'bg-red-100 text-red-800',
    medium: 'bg-yellow-100 text-yellow-800',
    low: 'bg-green-100 text-green-800'
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Merchant Processing Status</CardTitle>
            <CardDescription>Track merchant processing pipeline</CardDescription>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={onRefresh}
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {/* Search Bar */}
          <div className="flex gap-2">
            <Search className="w-4 h-4 text-muted-foreground mt-3" />
            <Input
              placeholder="Search by merchant name or URL..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              className="flex-1"
            />
            <span className="text-sm text-muted-foreground mt-3">
              {filteredMerchants.length} results
            </span>
          </div>

          {/* Table */}
          <div className="border rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted">
                  <TableHead className="font-semibold">Merchant</TableHead>
                  <TableHead className="font-semibold">Status</TableHead>
                  <TableHead className="font-semibold">Risk Level</TableHead>
                  <TableHead className="font-semibold">Stage</TableHead>
                  <TableHead className="font-semibold text-right">Time (s)</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8">
                      <Loader className="w-6 h-6 animate-spin mx-auto text-muted-foreground" />
                    </TableCell>
                  </TableRow>
                ) : paginatedMerchants.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                      No merchants found
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedMerchants.map((merchant) => (
                    <TableRow key={merchant.id} className="h-16">
                      <TableCell>
                        <div className="flex flex-col">
                          <span className="font-semibold text-base">{merchant.merchant_name}</span>
                          <span className="text-xs text-muted-foreground truncate">{merchant.merchant_url}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={statusColors[merchant.status] || statusColors.pending}>
                          {merchant.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge className={riskColors[merchant.risk_level] || riskColors.low}>
                          {merchant.risk_level}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm">{merchant.current_stage}</TableCell>
                      <TableCell className="text-right text-sm">
                        {merchant.processing_time_seconds}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">
                Page {currentPage} of {totalPages}
              </span>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// ============================================================================
// REPORT GENERATION DIALOG
// ============================================================================

function ReportGenerationDialog({ open, onOpenChange, onGenerate, loading }) {
  const [formData, setFormData] = useState({
    merchant_url: '',
    format: 'json',
    report_type: 'daily'
  });

  const handleGenerate = async () => {
    await onGenerate(formData);
    setFormData({ merchant_url: '', format: 'json', report_type: 'daily' });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Generate Report</DialogTitle>
          <DialogDescription>
            Create a report for a specific merchant
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium">Merchant URL</label>
            <Input
              placeholder="https://example.com"
              value={formData.merchant_url}
              onChange={(e) => setFormData({...formData, merchant_url: e.target.value})}
              className="mt-2"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium">Format</label>
              <select
                value={formData.format}
                onChange={(e) => setFormData({...formData, format: e.target.value})}
                className="w-full mt-2 px-3 py-2 border rounded-md"
              >
                <option value="json">JSON</option>
                <option value="pdf">PDF</option>
                <option value="excel">Excel</option>
              </select>
            </div>
            <div>
              <label className="text-sm font-medium">Report Type</label>
              <select
                value={formData.report_type}
                onChange={(e) => setFormData({...formData, report_type: e.target.value})}
                className="w-full mt-2 px-3 py-2 border rounded-md"
              >
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
              </select>
            </div>
          </div>
          <div className="flex gap-2 justify-end pt-4">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleGenerate}
              disabled={loading || !formData.merchant_url}
            >
              {loading ? (
                <>
                  <Loader className="w-4 h-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <FileText className="w-4 h-4 mr-2" />
                  Generate
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// MAIN ENTERPRISE DASHBOARD COMPONENT
// ============================================================================

export default function EnterpriseDashboard() {
  const [overviewData, setOverviewData] = useState(null);
  const [timeSeriesData, setTimeSeriesData] = useState([]);
  const [workflowStats, setWorkflowStats] = useState(null);
  const [workflowTimeline, setWorkflowTimeline] = useState([]);
  const [merchants, setMerchants] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [period, setPeriod] = useState('daily');
  const [timelinePeriod, setTimelinePeriod] = useState('daily');
  const [timelineDays, setTimelineDays] = useState(30);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [reportLoading, setReportLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [selectedReview, setSelectedReview] = useState(null);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);

  // Platform switcher uses viewing_tenant_id; empty = All Tenants (aggregate).
  const tenantId = localStorage.getItem('viewing_tenant_id') || '';
  const tenantQS = tenantId ? `tenant_id=${encodeURIComponent(tenantId)}` : '';
  const withTenant = (base) => {
    if (!tenantQS) return base;
    return `${base}${base.includes('?') ? '&' : '?'}${tenantQS}`;
  };

  useEffect(() => {
    fetchDashboardData();
    fetchWorkflowTimeline();
    fetchReviews();
  }, []);

  useEffect(() => {
    fetchTimeSeriesData();
  }, [period]);

  useEffect(() => {
    fetchWorkflowTimeline();
  }, [timelinePeriod, timelineDays]);

  const fetchReviews = async () => {
    try {
      // Fetch only approved and rejected reviews for the dashboard
      const [approvedRes, rejectedRes] = await Promise.all([
        fetch(`${API_BASE}/api/v1/workflow/reviews/?status=approved&limit=50`),
        fetch(`${API_BASE}/api/v1/workflow/reviews/?status=rejected&limit=50`)
      ]);
      
      const [approvedData, rejectedData] = await Promise.all([
        approvedRes.json(),
        rejectedRes.json()
      ]);
      
      let allReviews = [];
      
      if (approvedData.status === 'success') {
        allReviews = [...allReviews, ...(approvedData.reviews || [])];
      }
      
      if (rejectedData.status === 'success') {
        allReviews = [...allReviews, ...(rejectedData.reviews || [])];
      }
      
      // Sort by date (most recent first)
      allReviews.sort((a, b) => {
        const dateA = new Date(a.review_completed_at || a.updated_at || a.created_at);
        const dateB = new Date(b.review_completed_at || b.updated_at || b.created_at);
        return dateB - dateA;
      });
      
      console.log('Fetched reviews:', allReviews.length, 'reviews');
      console.log('Sample review:', allReviews[0]);
      setReviews(allReviews);
    } catch (error) {
      console.error('Error fetching reviews:', error);
    }
  };

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const [overview, workflowStatsRes, merchantStatus] = await Promise.all([
        fetch(withTenant(`${API_BASE}/api/v1/enterprise/dashboard/overview/`)).then(r => r.json()).catch(() => ({ total_merchants: 0, completed: 0, high_risk: 0, avg_processing_time: 0 })),
        fetch(`${API_BASE}/api/v1/workflow/statistics/`).then(r => r.json()).catch(() => ({ status: 'error' })),
        fetch(withTenant(`${API_BASE}/api/v1/enterprise/merchants/status/`)).then(r => r.json()).catch(() => ({ results: [] }))
      ]);

      setOverviewData(overview);
      
      // Transform workflow statistics to match expected format
      if (workflowStatsRes.status === 'success' && workflowStatsRes.statistics) {
        const stats = workflowStatsRes.statistics;
        setWorkflowStats({
          total_workflows: stats.total_reviews || 0,
          pending_approval: stats.pending_count || 0,
          approved: stats.approved_count || 0,
          rejected: stats.rejected_count || 0,
          avg_approval_time_hours: stats.avg_review_time_hours || 0
        });
      } else {
        setWorkflowStats({
          total_workflows: 0,
          pending_approval: 0,
          approved: 0,
          rejected: 0,
          avg_approval_time_hours: 0
        });
      }
      
      setMerchants(merchantStatus.results || []);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTimeSeriesData = async () => {
    try {
      const response = await fetch(
        withTenant(`${API_BASE}/api/v1/enterprise/dashboard/time-series/?period=${period}&days=7`)
      );
      const data = await response.json();
      setTimeSeriesData(data.data || []);
    } catch (error) {
      console.error('Error fetching time series data:', error);
    }
  };

  const fetchWorkflowTimeline = async () => {
    try {
      setTimelineLoading(true);
      const response = await fetch(
        `${API_BASE}/api/v1/workflow/analytics/timeline/?period=${timelinePeriod}&days=${timelineDays}`
      );
      const data = await response.json();
      if (data.status === 'success') {
        setWorkflowTimeline(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching workflow timeline:', error);
    } finally {
      setTimelineLoading(false);
    }
  };

  const handleGenerateReport = async (formData) => {
    try {
      setReportLoading(true);
      
      // Step 1: Generate the report
      const generateResponse = await fetch(`${API_BASE}/api/v1/enterprise/reports/generate/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tenant_id: tenantId,
          merchant_url: formData.merchant_url,
          report_type: formData.report_type,
          format: formData.format
        })
      });
      
      if (!generateResponse.ok) {
        throw new Error('Failed to generate report');
      }
      
      const generateData = await generateResponse.json();
      const reportId = generateData.report_id;
      
      // Step 2: Download the report
      const downloadResponse = await fetch(`${API_BASE}/api/v1/enterprise/reports/${reportId}/download/`);
      
      if (!downloadResponse.ok) {
        throw new Error('Failed to download report');
      }
      
      // Get filename from Content-Disposition header
      const contentDisposition = downloadResponse.headers.get('Content-Disposition');
      let filename = `report_${formData.merchant_url.replace(/[^a-z0-9]/gi, '_')}_${formData.format}.${formData.format === 'excel' ? 'xlsx' : formData.format}`;
      if (contentDisposition) {
        const matches = /filename[^;=\\n]*=((['\"]).*?\\2|[^;\\n]*)/.exec(contentDisposition);
        if (matches != null && matches[1]) {
          filename = matches[1].replace(/['\\"]/g, '');
        }
      }
      
      // Download the file
      const blob = await downloadResponse.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      setSuccessMessage(`✅ Report generated and downloaded successfully!`);
      setReportDialogOpen(false);
      setTimeout(() => setSuccessMessage(''), 5000);
      
    } catch (error) {
      console.error('Error generating report:', error);
      alert(`❌ Error: ${error.message}`);
    } finally {
      setReportLoading(false);
    }
  };

  return (
    <div className="max-w-[1500px] mx-auto space-y-6 pb-8">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold">Enterprise Analytics</h1>
          <p className="text-muted-foreground">Workflow approvals and merchant reviews</p>
        </div>
        <Button onClick={() => setReportDialogOpen(true)} size="lg">
          <FileText className="w-4 h-4 mr-2" />
          Generate Report
        </Button>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="bg-green-100 text-green-800 p-4 rounded-lg flex items-center gap-2">
          <CheckCircle className="w-5 h-5" />
          {successMessage}
        </div>
      )}

      {/* Approval Summary Boxes */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <div className="text-4xl font-bold text-green-600">
                {workflowStats?.approved || 0}
              </div>
              <p className="text-sm text-muted-foreground mt-2">Approved</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <div className="text-4xl font-bold text-red-600">
                {workflowStats?.rejected || 0}
              </div>
              <p className="text-sm text-muted-foreground mt-2">Rejected</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <div className="text-4xl font-bold text-amber-600">
                {workflowStats?.pending_approval || 0}
              </div>
              <p className="text-sm text-muted-foreground mt-2">Pending</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <div className="text-4xl font-bold text-blue-600">
                {workflowStats?.total_workflows || 0}
              </div>
              <p className="text-sm text-muted-foreground mt-2">Total Reviews</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Approval Status and Status Distribution (side by side) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader>
            <CardTitle>Approval Status</CardTitle>
            <CardDescription>Workflow instances by status</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm">Total Workflows</span>
                <Badge variant="outline">{workflowStats?.total_workflows || 0}</Badge>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-amber-600">Pending Approval</span>
                <Badge className="bg-amber-100 text-amber-800">{workflowStats?.pending_approval || 0}</Badge>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-green-600">Approved</span>
                <Badge className="bg-green-100 text-green-800">{workflowStats?.approved || 0}</Badge>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-red-600">Rejected</span>
                <Badge className="bg-red-100 text-red-800">{workflowStats?.rejected || 0}</Badge>
              </div>
              <hr className="my-3" />
              <div className="flex justify-between items-center">
                <span className="text-sm font-semibold">Avg Approval Time</span>
                <span className="font-semibold">{workflowStats?.avg_approval_time_hours || 0}h</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Status Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie
                  data={[
                    { name: 'Pending', value: workflowStats?.pending_approval || 0 },
                    { name: 'Approved', value: workflowStats?.approved || 0 },
                    { name: 'Rejected', value: workflowStats?.rejected || 0 }
                  ]}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, value }) => value > 0 ? `${name}: ${value}` : ''}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  <Cell fill="#f59e0b" />
                  <Cell fill="#10b981" />
                  <Cell fill="#ef4444" />
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Compact Timeline and Status Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Approval Timeline</CardTitle>
              <div className="flex gap-1">
                {['daily', 'weekly', 'monthly'].map(p => (
                  <Button
                    key={p}
                    variant={timelinePeriod === p ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => {
                      setTimelinePeriod(p);
                      setTimelineDays(p === 'daily' ? 30 : p === 'weekly' ? 90 : 180);
                    }}
                    className="capitalize text-xs"
                  >
                    {p}
                  </Button>
                ))}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={160}>
              <AreaChart data={workflowTimeline}>
                <defs>
                  <linearGradient id="wfCompactApproved" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0.05} />
                  </linearGradient>
                  <linearGradient id="wfCompactRejected" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#ef4444" stopOpacity={0.25} />
                    <stop offset="95%" stopColor="#ef4444" stopOpacity={0.05} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#6b7280' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 10, fill: '#6b7280' }} axisLine={false} tickLine={false} allowDecimals={false} />
                <Tooltip />
                <Area type="monotone" dataKey="approved" stroke="#10b981" strokeWidth={2} fill="url(#wfCompactApproved)" dot={false} name="Approved" />
                <Area type="monotone" dataKey="rejected" stroke="#ef4444" strokeWidth={2} fill="url(#wfCompactRejected)" dot={false} name="Rejected" />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Status Breakdown</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={160}>
              <BarChart data={workflowTimeline.slice(-10)} barSize={14}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#6b7280' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 10, fill: '#6b7280' }} axisLine={false} tickLine={false} allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="approved" fill="#10b981" radius={[6, 6, 0, 0]} name="Approved" />
                <Bar dataKey="rejected" fill="#ef4444" radius={[6, 6, 0, 0]} name="Rejected" />
                <Bar dataKey="in_progress" fill="#f59e0b" radius={[6, 6, 0, 0]} name="In Progress" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Merchant Approval/Rejection List */}
      <Card>
        <CardHeader>
          <CardTitle>Merchant Reviews</CardTitle>
          <CardDescription>Recent approved and rejected merchants with review comments</CardDescription>
        </CardHeader>
        <CardContent>
          {reviews.length > 0 ? (
            <div className="space-y-3">
              {reviews.slice(0, 10).map((review, index) => {
                const status = review.current_status || review.status || 'pending';
                const reviewDate = review.review_completed_at || review.updated_at || review.created_at;
                
                return (
                  <div key={review.id || index} className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 transition-colors">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="font-semibold text-sm truncate">{review.merchant_url || 'Unknown merchant'}</span>
                        <Badge 
                          variant={
                            status === 'approved' ? 'default' :
                            status === 'rejected' ? 'destructive' : 'secondary'
                          }
                          className="shrink-0"
                        >
                          {status}
                        </Badge>
                      </div>
                      {review.comments && (
                        <p className="text-xs text-muted-foreground line-clamp-2 mb-1">{review.comments}</p>
                      )}
                      <p className="text-xs text-muted-foreground">
                        Reviewed on {
                          reviewDate 
                            ? new Date(reviewDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                            : 'Date unknown'
                        }
                      </p>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        console.log('Opening review dialog for:', review);
                        setSelectedReview(review);
                        setReviewDialogOpen(true);
                      }}
                      className="ml-3 shrink-0"
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <FileText className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>No reviews yet</p>
              <p className="text-xs mt-1">Reviews will appear here once merchants are approved or rejected</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Review Details Dialog */}
      <Dialog open={reviewDialogOpen} onOpenChange={setReviewDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Review Details</DialogTitle>
            <DialogDescription>{selectedReview?.merchant_url || 'N/A'}</DialogDescription>
          </DialogHeader>
          {selectedReview ? (
            <div className="space-y-4">
              <div>
                <label className="text-sm font-semibold text-gray-700 block mb-1">Status</label>
                <Badge variant={
                  (selectedReview.current_status || selectedReview.status) === 'approved' ? 'default' :
                  (selectedReview.current_status || selectedReview.status) === 'rejected' ? 'destructive' : 'secondary'
                } className="text-sm">
                  {(selectedReview.current_status || selectedReview.status || 'PENDING').toUpperCase()}
                </Badge>
              </div>
              <div>
                <label className="text-sm font-semibold text-gray-700 block mb-1">Review Started</label>
                <p className="text-sm text-gray-900">
                  {selectedReview.review_started_at 
                    ? new Date(selectedReview.review_started_at).toLocaleString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })
                    : 'Not started'}
                </p>
              </div>
              <div>
                <label className="text-sm font-semibold text-gray-700 block mb-1">Review Completed</label>
                <p className="text-sm text-gray-900">
                  {selectedReview.review_completed_at 
                    ? new Date(selectedReview.review_completed_at).toLocaleString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })
                    : selectedReview.updated_at
                    ? new Date(selectedReview.updated_at).toLocaleString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })
                    : 'Not completed'}
                </p>
              </div>
              <div>
                <label className="text-sm font-semibold text-gray-700 block mb-1">Workflow</label>
                <p className="text-sm text-gray-900">
                  {selectedReview.workflow_name || `Workflow ID: ${selectedReview.workflow_id}` || 'Not specified'}
                </p>
              </div>
              {selectedReview.report_id && (
                <div>
                  <label className="text-sm font-semibold text-gray-700 block mb-1">Report ID</label>
                  <p className="text-sm text-gray-900">{selectedReview.report_id}</p>
                </div>
              )}
              {selectedReview.comments && (
                <div>
                  <label className="text-sm font-semibold text-gray-700 block mb-1">Comments</label>
                  <div className="text-sm mt-1 p-3 bg-gray-50 border border-gray-200 rounded-md text-gray-900">
                    {selectedReview.comments}
                  </div>
                </div>
              )}
              {selectedReview.approval_feedback && (
                <div>
                  <label className="text-sm font-semibold text-gray-700 block mb-1">Approval Feedback</label>
                  <div className="text-sm mt-1 p-3 bg-blue-50 border border-blue-200 rounded-md text-gray-900">
                    {selectedReview.approval_feedback}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <p>No review data available</p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Report Generation Dialog */}
      <ReportGenerationDialog
        open={reportDialogOpen}
        onOpenChange={setReportDialogOpen}
        onGenerate={handleGenerateReport}
        loading={reportLoading}
      />
    </div>
  );
}
